import { useMemo } from "react";

import loggingService from "@app/util/logging";

import Iam from "../../types/Iam";

export const useExtendedAnalytics = (
  key: string | string[],
  analyticIssuer?: AnalyticsService
): AnalyticsService =>
  useMemo(
    () => (analyticIssuer ? analyticIssuer.extend(key) : new AnalyticsService(key)),
    // we do have all deps in deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analyticIssuer, ...(Array.isArray(key) ? key : [key])]
  );

export type EventListener = (event: string, payload: any) => void;

export class AnalyticsService {
  keyPath: string[] = [];
  static traits: { orgId?: number; userId?: number } = {};
  static shouldTrack: boolean = true; // whether we should track any events
  static listeners: Array<{ event: RegExp | string; listener: EventListener }> = [];

  constructor(keyPath?: string | string[]) {
    if (keyPath) {
      this.keyPath = Array.isArray(keyPath) ? keyPath : [keyPath];
    }
  }

  extend = (keyPath: string | string[]) =>
    new AnalyticsService([...this.keyPath, ...(Array.isArray(keyPath) ? keyPath : [keyPath])]);

  callSegmentMethod = (method: keyof SegmentAnalytics.AnalyticsJS, ...parameters: any[]) => {
    if (!AnalyticsService.shouldTrack) {
      return;
    }

    if (
      window.analytics &&
      window.analytics[method] &&
      typeof window.analytics[method] === "function"
    ) {
      // @ts-ignore
      window.analytics[method](...parameters, {
        groupId: AnalyticsService.traits.orgId,
        traits: AnalyticsService.traits,
        userId: AnalyticsService.traits.userId,
      });
    }
    if (process.env.REACT_APP_ANALYTICS_TRACKING_DEBUG) {
      loggingService.debug(`Analytics [${method}]: ${parameters.length > 0 ? parameters[0] : ""}`, {
        event: parameters.length > 1 ? parameters[1] : undefined,
        orgId: AnalyticsService.traits.orgId,
        userId: AnalyticsService.traits.userId,
      });
    }
  };

  identify = (user: Iam) => {
    const traits = {
      addOnServices: user.organization.addOnServices,
      createdAt: user.createdAt,
      crmType: user.organization.metaData?.crm,
      deactivated: user.deactivated,
      email: user.username,
      features: user.organization.metaData?.features,
      fullName: user.fullName,
      lastActivity: user.lastActivity,
      marketingEnabled: user.marketingEnabled,
      maxNumberOfUserSeats: user.organization.plan.quantity,
      numFieldReps: user.organization.metaData?.numFieldReps ?? user.organization.numFieldReps,
      orgId: user.organization.id,
      orgName: user.organization.name,
      phone: user.phone,
      planID: user.organization.plan.id,
      planName: user.organization.plan.name,
      planPrice: user.organization.plan.price,
      renewalDate: user.organization.plan.nextPaymentDate,
      role: user.role.name,
      "sign-upSource": user["sign-upSource"],
      teamSize: user.organization.teamSize,
      trialExpiresAt: user.organization.trialExpiresAt,
      userId: user.id,
    };
    this.callSegmentMethod("identify", user.id, traits);
    AnalyticsService.traits = traits;
  };

  group = (user: Iam) => {
    this.callSegmentMethod("group", user.organization.id, {
      domain: user.organization.domain,
      name: user.organization.name,
      planID: user.organization.plan.id,
      planName: user.organization.plan.name,
      planPrice: user.organization.plan.price,
      renewalDate: user.organization.plan.nextPaymentDate,
      trialExpiresAt: user.organization.trialExpiresAt,
    });
  };

  page = () => {
    this.callSegmentMethod("page");
  };

  track = (event: string, properties = {}) => {
    const finalProperties = {
      ...properties,
      groupId: AnalyticsService.traits.orgId,
      userId: AnalyticsService.traits.userId,
    };
    this.callSegmentMethod("track", event, finalProperties);

    AnalyticsService.listeners.forEach((item) => {
      if (typeof item.event === "string") {
        if (item.event === event) {
          item.listener(event, properties);
        }
      } else {
        if (item.event.test(event)) {
          item.listener(event, properties);
        }
      }
    });
  };

  getEventName = (event: string | string[]) =>
    [...this.keyPath, ...(Array.isArray(event) ? event : event.trim().length ? [event] : [])].join(
      " -> "
    );

  initiated = (event: string | string[] = "", properties = {}) => {
    this.track(`Process: ${this.getEventName(event)}`, { status: "initiated", ...properties });
  };

  completed = (event: string | string[] = "", properties = {}) => {
    this.track(`Process: ${this.getEventName(event)}`, { status: "completed", ...properties });
  };

  cancelled = (event: string | string[] = "", properties = {}) => {
    this.track(`Process: ${this.getEventName(event)}`, { status: "cancelled", ...properties });
  };

  failed = (event: string | string[] = "", properties = {}) => {
    this.track(`Process: ${this.getEventName(event)}`, { status: "failed", ...properties });
  };

  feature = (featurePath: string[], properties = {}) => {
    this.track(`Feature: ${this.getEventName(featurePath)}`, properties);
  };

  clicked = (featurePath: string[], properties = {}) => {
    this.track(`Clicked: ${this.getEventName(featurePath)}`, properties);
  };

  using = (featurePath: string[], properties = {}) => {
    this.track(`Using: ${this.getEventName(featurePath)}`, properties);
  };

  static addEventListener = (event: RegExp | string, listener: EventListener) => {
    AnalyticsService.listeners.push({ event, listener });
  };

  static removeEventListener = (event: RegExp | string, listener: EventListener) => {
    AnalyticsService.listeners = AnalyticsService.listeners.filter(
      (item) => !(item.listener === listener && event.toString() === item.event.toString())
    );
  };
}

const analyticsService = new AnalyticsService([]);

export default analyticsService;
